<script setup>
import { onMounted, ref, nextTick, computed, watch } from "vue";
import { useRoute } from "vue-router";
import Swal from "sweetalert2";

import Sortable from "sortablejs";
import { ChaptersClient } from "@/services/ChaptersClient";
import { LessonsClient } from "@/services/LessonsClient";
import { QuizzesClient } from "@/services/QuizzesClient";
const route = useRoute();

const isLoading = ref(false);
const isLoadingUnits = ref({}); // Nuevo estado para carga de unidades por capítulo
const isCreatingLesson = ref({}); // Guard para evitar doble-click al crear lección
const isCreatingQuiz = ref({});   // Guard para evitar doble-click al crear quiz

import ChapterEdit from './ChapterEdit.vue';
import LessonEdit from './LessonEdit.vue';
import QuizMaker from './QuizMaker.vue';

const activeTab = ref(null) // Can be 'chapter', 'lesson', or 'quiz'
const selectedChapterId = ref(null)
const selectedLessonId = ref(null)

const chaptersClient = new ChaptersClient();
const lessonsClient = new LessonsClient();
const quizzesClient = new QuizzesClient();

const chapters = ref([]);
const lessons = ref([]); // Initialize with your lessons data

const currentChapterData = computed(() => {
  return chapters.value.find(c => c.id === selectedChapterId.value);
});

const currentLessonData = computed(() => {
  if (!selectedChapterId.value) return null;
  const chapter = chapters.value.find(c => c.id === selectedChapterId.value);
  if (!chapter) return null;
  return chapter.lessons.find(l => l.id === selectedLessonId.value);
});

const currentQuizData = computed(() => {
  if (!selectedChapterId.value) return null;
  const chapter = chapters.value.find(c => c.id === selectedChapterId.value);
  if (!chapter) return null;

  // Find the lesson and verify it's a quiz
  const quiz = chapter.lessons.find(l => l.id === selectedLessonId.value);
  return quiz?.type === 'quiz' ? quiz : null;
});

// Modified editChapter function to handle switching
const editChapter = async (id) => {
  // If already editing this chapter, no need to change
  if (selectedChapterId.value === id && activeTab.value === 'chapter') return;

  activeTab.value = 'chapter';
  selectedChapterId.value = id;
  selectedLessonId.value = null;

  // Find the chapter and fetch content if needed
  const chapter = chapters.value.find(c => c.id === id);
  if (chapter) {
    if (!chapter.lessons || chapter.lessons.length === 0) {
      await fetchLessons(id);
      await fetchQuizzes(id); // Also fetch quizzes when opening
    }
  }
};

const toggleChapter = async (chapterId) => {
  const chapter = chapters.value.find(c => c.id === chapterId);
  if (chapter) {
    chapter.isOpen = !chapter.isOpen;
    if (chapter.isOpen) {
      // Mostrar estado de carga para este capítulo
      isLoadingUnits.value[chapterId] = true;

      try {
        if (!chapter.lessons || chapter.lessons.length === 0) {
          await fetchLessons(chapterId);
          await fetchQuizzes(chapterId);
        } 
        
      } finally {
        // Ocultar estado de carga
        isLoadingUnits.value[chapterId] = false;
      }
    }
  }
};

// Modified editLesson function to handle switching
const editLesson = async (chapterId, lessonId) => {
  
  // Reset selection first
  selectedLessonId.value = null;
  activeTab.value = null;
  
  // Wait for next tick to ensure Vue processes the change
  await nextTick();
  
  const chapter = chapters.value.find(c => c.id === chapterId);
  if (!chapter) return;

  const lesson = chapter.lessons.find(l => l.id === lessonId);
  console.log("Editing 2: ", lesson);

  if (!lesson) return;

  activeTab.value = lesson.type;
  selectedChapterId.value = chapterId;
  selectedLessonId.value = lessonId;
};

const editQuiz = async (chapterId, quizId) => {
  // Reset selection first
  selectedLessonId.value = null;
  activeTab.value = null;

  // Wait for next tick
  await nextTick();

  const chapter = chapters.value.find(c => c.id === chapterId);
  if (!chapter) return;

  const quiz = chapter.lessons.find(l => l.id === quizId && l.type === 'quiz');
  if (!quiz) return;

  activeTab.value = 'quiz';
  selectedChapterId.value = chapterId;
  selectedLessonId.value = quizId;
};

// Add this watcher to handle chapter data updates
watch(selectedChapterId, (newId) => {
  if (newId && activeTab.value === 'chapter') {
    // Ensure the chapter is expanded when editing
    const chapter = chapters.value.find(c => c.id === newId);
    if (chapter) chapter.isOpen = true;
  }
});

// Method to add a new chapter
// In your component
const addNewChapter = async (newChapterData) => {
  try {
    // Validate input
    if (!newChapterData?.title?.trim()) {
      throw new Error('Chapter title is required');
    }

    // Calculate the next order number (max existing order + 1)
    const maxOrder = chapters.value.reduce((max, chapter) =>
      Math.max(max, chapter.orden || 0), 0);
    const nextOrder = maxOrder + 1;

    // Build DTO with calculated order
    const createDto = {
      curso_id: Number(route.params.id),
      titulo: newChapterData.title.trim(),
      orden: nextOrder, // Use calculated order instead of optional value
      es_gratuito: newChapterData.isFree ? 1 : 0
    };

    // Make request
    const response = await chaptersClient.createChapter(createDto);

    // Handle response
    if (!response?.id) {
      throw new Error('Invalid response from server');
    }

    // Update local state
    const chapterToAdd = {
      id: response.id,
      titulo: response.titulo,
      orden: response.orden || nextOrder, // Fallback to our calculated order
      es_gratuito: response.es_gratuito,
      lessons: [],
      isOpen: true
    };

    chapters.value = [...chapters.value, chapterToAdd]
      .sort((a, b) => a.orden - b.orden);

    // Automatically select and edit the new chapter
    selectedChapterId.value = chapterToAdd.id;
    activeTab.value = 'chapter';

    return chapterToAdd;

  } catch (error) {
    console.error("Creation failed:", {
      error: error.message,
      response: error.response?.data,
      request: error.config
    });
    throw new Error(error.response?.data?.message || 'Failed to create chapter');
  }
};

// Method to update an existing chapter
const updateChapter = async (chapterId, updateData) => {
  try {
    if (!updateData || typeof updateData !== 'object') {
      throw new Error('Invalid update data');
    }

    const updateDto = {
      titulo: updateData.title || undefined, // fallback to undefined if title doesn't exist
      orden: updateData.orden,
      es_gratuito: updateData.isFree ? 1 : 0
    };


    const response = await chaptersClient.updateChapter(chapterId, updateDto);


    // Update local state
    const index = chapters.value.findIndex(c => c.id === chapterId);
    if (index !== -1) {
      const prereq = await chaptersClient.getPrerequisites(chapterId);
      chapters.value[index] = {
        ...chapters.value[index],
        ...response,
        hasPrerequisite: prereq && prereq.length > 0
      };
    }
    

    return response;
  } catch (error) {
    console.error("Error updating chapter:", error);
    throw error;
  }
};

// Method to delete a chapter
const removeChapter = async (chapterId) => {
  try {
    // 1. Call API (with empty response handling)
    await chaptersClient.deleteChapter(chapterId)
      .catch(e => {
        if (!e.message.includes('JSON')) throw e; // Ignore JSON parse errors
      });

    // 2. Update local state
    chapters.value = chapters.value.filter(c => c.id !== chapterId);

    // 3. Reset selection if needed
    if (selectedChapterId.value === chapterId) {
      selectedChapterId.value = null;
      activeTab.value = null;
    }

    // 4. Show success
    await Swal.fire({
      title: '¡Eliminado!',
      text: 'El capítulo ha sido eliminado.',
      icon: 'success',
      timer: 1500,
      showConfirmButton: false
    });

  } catch (error) {
    console.error("Delete error:", error);
    await Swal.fire({
      title: 'Error',
      text: error.message || 'No se pudo eliminar el capítulo',
      icon: 'error'
    });
  }
};

const reorderChapters = async (newOrderContainer) => {
  try {
    // Extract the new orden from the DOM elements
    const orden = Array.from(newOrderContainer.children).map((el, index) => ({
      id: parseInt(el.dataset.id),
      orden: index + 1 // 1-based index
    }));



    // Optimistically update local state first
    orden.forEach(item => {
      const chapter = chapters.value.find(c => c.id === item.id);
      if (chapter) chapter.orden = item.orden;
    });

    // Sort chapters locally for immediate UI update
    chapters.value.sort((a, b) => a.orden - b.orden);

    // Call API to persist changes
    await chaptersClient.reorderChapters(
      orden.map(item => ({
        id: item.id,
        orden: item.orden
      }))
    );


  } catch (error) {
    console.error("Error reordering chapters:", error);
    // Optionally revert local state on error
    await fetchChapters(); // Refresh from server to undo optimistic update

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo guardar el nuevo orden'
    });
  }
};

const addNewLesson = async (chapterId, newLessonData) => {
  // Guard: evitar doble-click mientras se procesa la creación
  if (isCreatingLesson.value[chapterId]) return;

  try {
    isCreatingLesson.value[chapterId] = true;

    const chapter = chapters.value.find(c => c.id === chapterId);
    const nextOrder = (chapter?.lessons?.length || 0) + 1;

    const createDto = {
      capitulo_id: Number(chapterId),
      titulo: newLessonData.title?.trim() || 'Nueva Lección',
      orden: nextOrder,
      contenido_texto: newLessonData.content || '',
      es_gratuito: newLessonData.isFree ? 1 : 0,
      duracion: newLessonData.duration || 0
    };

    const response = await lessonsClient.createLesson(createDto);

    if (!response?.id) {
      throw new Error('Invalid lesson creation response');
    }

    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex !== -1) {
      const lessonToAdd = {
        ...response,
        orden: response.orden ?? nextOrder,
        title: response.titulo, // Ensure title exists
        type: 'lesson', // Explicitly set type
        contenido_texto: response.contenido_texto || createDto.contenido_texto,
        es_gratuito: response.hasOwnProperty('es_gratuito') ? response.es_gratuito : createDto.es_gratuito,
        duracion: response.duracion || createDto.duracion
      };

      chapters.value = chapters.value.map((chapter, idx) => {
        if (idx === chapterIndex) {
          return {
            ...chapter,
            lessons: [...(chapter.lessons || []), lessonToAdd]
              .sort((a, b) => a.orden - b.orden)
          };
        }
        return chapter;
      });

      selectedLessonId.value = response.id;
      activeTab.value = 'lesson';

      return lessonToAdd;
    }

    throw new Error('Chapter not found after creation');
  } catch (error) {
    console.error("Lesson creation failed:", error);

    // Re-sincronizar con el servidor: el error 500 puede ocurrir DESPUÉS de que
    // la lección ya fue insertada en la BD. Al re-fetchear, el usuario ve la lección
    // creada y no la duplica con un segundo intento.
    try {
      await fetchLessons(chapterId);
    } catch (fetchError) {
      console.error("Error re-syncing lessons after failed creation:", fetchError);
    }

    Swal.fire({
      icon: 'error',
      title: 'Error al crear lección',
      text: 'Hubo un problema con el servidor. Por favor revisa si la lección fue creada antes de intentarlo de nuevo.',
    });
    // No re-lanzar para no interrumpir más el flujo
  } finally {
    isCreatingLesson.value[chapterId] = false;
  }
};

// Method to update an existing lesson
const updateLesson = async (lessonId, updateData) => {
  try {
    if (!updateData || typeof updateData !== 'object') {
      throw new Error('Invalid update data');
    }

    const updateDto = {
      titulo: updateData.title,
      orden: updateData.orden,
      contenido_texto: updateData.contenido_texto,
      es_gratuito: updateData.isFree ? 1 : 0
    };


    // 1. API call
    const response = await lessonsClient.updateLesson(lessonId, updateDto);

    // 2. Update local state with proper array checks
    chapters.value = chapters.value.map(chapter => {
      if (!chapter.lessons || !Array.isArray(chapter.lessons)) {
        return chapter; // Skip if no lessons array exists
      }

      const lessonIndex = chapter.lessons.findIndex(l => l.id === lessonId);
      if (lessonIndex === -1) return chapter;

      const updatedLessons = [...chapter.lessons];
      updatedLessons[lessonIndex] = {
        ...updatedLessons[lessonIndex],
        ...updateDto
      };

      return {
        ...chapter,
        lessons: updatedLessons.sort((a, b) => a.orden - b.orden)
      };
    });

    return response;
  } catch (error) {
    console.error("Error updating lesson:", error.message);
    throw error;
  }
};

// In parent component
const removeLesson = async ({ chapterId, lessonId }) => {
  try {

    // 1. Validate inputs
    if (!lessonId) {
      throw new Error('ID de lección no proporcionados');
    }

    // 2. Find the chapter
    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex === -1) {
      throw new Error('Capítulo no encontrado');
    }

    // 3. Find the lesson (optional - can do API call directly)
    const lessonExists = chapters.value[chapterIndex].lessons.some(
      l => l.id === lessonId
    );
    if (!lessonExists) {
      throw new Error('Lección no encontrada');
    }

    // 4. API call to delete 
    await lessonsClient.deleteLesson(lessonId)
      .catch(e => {
        if (!e.message.includes('JSON')) throw e; // Ignore JSON parse errors
      });

    // 5. Update local state
    chapters.value[chapterIndex].lessons = chapters.value[chapterIndex].lessons.filter(
      l => l.id !== lessonId
    );

    // 6. Clean up selections if needed
    if (selectedLessonId.value === lessonId) {
      selectedLessonId.value = null;
      activeTab.value = null;
    }

    // 7. Show success
    Swal.fire('¡Éxito!', 'Lección eliminada correctamente', 'success');

  } catch (error) {
    console.error('Error deleting lesson:', error);
    Swal.fire('Error', error.message || 'Error al eliminar la lección', 'error');

    // Optional: Revert optimistic updates here if you implemented them
  }
};

const handleUnitsReordered = ({ chapterId, lessons, quizzes }) => {
  const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
  if (chapterIndex !== -1) {
    // Preserve type and title properties when reordering
    const updatedLessons = lessons.map(l => ({
      ...l,
      title: l.titulo || l.title,
      type: l.type || 'lesson'
    }));
    
    const updatedQuizzes = quizzes.map(q => ({
      ...q,
      title: q.titulo || q.title,
      type: q.type || 'quiz'
    }));

    chapters.value[chapterIndex].lessons = [...updatedLessons, ...updatedQuizzes]
      .sort((a, b) => a.orden - b.orden);
  }
};

const addNewQuiz = async (chapterId, quizData) => {
  // Guard: evitar doble-click mientras se procesa la creación
  if (isCreatingQuiz.value[chapterId]) return;

  try {
    isCreatingQuiz.value[chapterId] = true;

    const chapter = chapters.value.find(c => c.id === chapterId);
    const nextOrder = (chapter?.lessons?.length || 0) + 1;

    const createDto = {
      capitulo_id: Number(chapterId),
      titulo: quizData.title?.trim() || 'Nuevo Quiz',
      orden: nextOrder,
      contenido_texto: quizData.content || '',
      es_gratuito: quizData.isFree ? 1 : 0,
      tipo: 'quiz'
    };

    const response = await quizzesClient.createQuiz(createDto);

    if (!response?.id) {
      throw new Error('Invalid quiz creation response');
    }

    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex !== -1) {
      const quizToAdd = {
        ...response,
        orden: response.orden ?? nextOrder,
        title: response.titulo, // Ensure title exists
        type: 'quiz', // Explicitly set type
        contenido_texto: response.contenido_texto || createDto.contenido_texto,
        es_gratuito: response.hasOwnProperty('es_gratuito') ? response.es_gratuito : createDto.es_gratuito
      };

      chapters.value = chapters.value.map((chapter, idx) => {
        if (idx === chapterIndex) {
          return {
            ...chapter,
            lessons: [...(chapter.lessons || []), quizToAdd]
              .sort((a, b) => a.orden - b.orden)
          };
        }
        return chapter;
      });

      selectedLessonId.value = response.id;
      activeTab.value = 'quiz';

      return quizToAdd;
    }

    throw new Error('Chapter not found after quiz creation');
  } catch (error) {
    console.error("Quiz creation failed:", error);

    // Re-sincronizar con el servidor por el mismo motivo que en addNewLesson
    try {
      await fetchQuizzes(chapterId);
    } catch (fetchError) {
      console.error("Error re-syncing quizzes after failed creation:", fetchError);
    }

    Swal.fire({
      icon: 'error',
      title: 'Error al crear quiz',
      text: 'Hubo un problema con el servidor. Por favor revisa si el quiz fue creado antes de intentarlo de nuevo.',
    });
    // No re-lanzar para no interrumpir más el flujo
  } finally {
    isCreatingQuiz.value[chapterId] = false;
  }
};

const updateQuiz = async (quizId, updateData) => {
  try {
    if (!updateData || typeof updateData !== 'object') {
      throw new Error('Invalid update data');
    }

    const updateDto = {
      titulo: updateData.titulo || updateData.title,
      instrucciones: updateData.instrucciones || '',
      es_gratuito: updateData.isFree ? 1 : 0,
      preguntas: updateData.preguntas || []
    };


    // 1. API call
    const response = await quizzesClient.updateQuiz(quizId, updateDto);

    // 2. Update local state with proper array checks
    chapters.value = chapters.value.map(chapter => {
      if (!chapter.lessons || !Array.isArray(chapter.lessons)) {
        return chapter; // Skip if no lessons array exists
      }

      const quizIndex = chapter.lessons.findIndex(
        l => l.id === quizId && l.type === 'quiz'
      );
      if (quizIndex === -1) return chapter;

      const updatedLessons = [...chapter.lessons];
      updatedLessons[quizIndex] = {
        ...updatedLessons[quizIndex],
        ...updateDto,
        type: 'quiz' // Maintain quiz type
      };

      return {
        ...chapter,
        lessons: updatedLessons.sort((a, b) => a.orden - b.orden)
      };
    });

    return response;
  } catch (error) {
    console.error("Error updating quiz:", error.message);
    throw error;
  }
};

// Method to delete a quiz
const removeQuiz = async ({ chapterId, quizId }) => {
  try {
    // 1. Validate inputs
    if (!quizId) {
      throw new Error('ID de quiz no proporcionado');
    }

    // 2. Find the chapter index
    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex === -1) {
      throw new Error('Capítulo no encontrado');
    }

    // 3. Verify quiz exists
    const quizExists = chapters.value[chapterIndex].lessons?.some(
      l => l.id === quizId && l.type === 'quiz'
    );
    if (!quizExists) {
      throw new Error('Quiz no encontrado');
    }

    // 4. API call to delete with JSON error handling
    await quizzesClient.deleteQuiz(quizId)
      .catch(e => {
        if (!e.message.includes('JSON')) throw e;
      });

    // 5. Update local state immutably
    chapters.value = chapters.value.map((chapter, index) => {
      if (index === chapterIndex) {
        return {
          ...chapter,
          lessons: chapter.lessons?.filter(l => l.id !== quizId) || []
        };
      }
      return chapter;
    });

    // 6. Clean up selections if needed
    if (selectedLessonId.value === quizId) {
      selectedLessonId.value = null;
      activeTab.value = null;
    }

    // 7. Show success
    Swal.fire('¡Éxito!', 'Quiz eliminado correctamente', 'success');

  } catch (error) {
    console.error('Error deleting quiz:', error);
    Swal.fire('Error', error.message || 'Error al eliminar el quiz', 'error');
  }
};

const fetchChapters = async () => {
  try {
    isLoading.value = true;
    const courseId = Number(route.params.id);
    const response = await chaptersClient.getAllChaptersByCourse(courseId);

    // Transform response
    chapters.value = response.map(chapter => ({
      id: chapter.id,
      titulo: chapter.titulo,
      orden: chapter.orden,
      es_gratuito: chapter.es_gratuito,
      isOpen: false,
      hasPrerequisite: false, // NEW FLAG
      lessons: chapter.lessons || []
    }));

    // Now fetch prerequisites for each chapter
    for (const chapter of chapters.value) {
      try {
        const prereq = await chaptersClient.getPrerequisites(chapter.id);
        chapter.hasPrerequisite = prereq && prereq.length > 0;
      } catch (err) {
        console.error(`Error fetching prerequisites for chapter ${chapter.id}`, err);
        chapter.hasPrerequisite = false;
      }
    }

  } catch (error) {
    console.error("Error fetching chapters:", error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar los capítulos'
    });
  } finally {
    isLoading.value = false;
  }
};

// Method to fetch lessons for a specific chapter
const fetchLessons = async (chapterId) => {
  try {
    isLoadingUnits.value[chapterId] = true;
    const response = await lessonsClient.getAllLessonsByChapter(chapterId);

    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex !== -1) {
      chapters.value[chapterIndex].lessons = response.map((lesson, index) => ({
        ...lesson,
        orden: lesson.orden ?? index + 1, // Fallback si el servidor no devuelve orden
        title: lesson.titulo || lesson.title, // Ensure title exists
        type: 'lesson' // Explicitly set type
      }));
    }

    return response;
  } catch (error) {
    console.error(`Error fetching lessons for chapter ${chapterId}:`, error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar las lecciones'
    });
    throw error;
  } finally {
    isLoadingUnits.value[chapterId] = false;
  }
};

const fetchQuizzes = async (chapterId) => {
  try {
    isLoadingUnits.value[chapterId] = true;
    const response = await quizzesClient.getAllQuizzesByChapter(chapterId);

    const chapterIndex = chapters.value.findIndex(c => c.id === chapterId);
    if (chapterIndex !== -1) {
      const quizzes = response.map(quiz => ({
        ...quiz,
        titulo: quiz.titulo || quiz.title || 'Nuevo Quiz',
        title: quiz.titulo || quiz.title || 'Nuevo Quiz', // Ensure title exists
        type: 'quiz' // Explicitly set type
      }));

      const existingLessons = chapters.value[chapterIndex].lessons || [];
      const nonQuizLessons = existingLessons.filter(l => l.type !== 'quiz');
      
      chapters.value[chapterIndex].lessons = [...nonQuizLessons, ...quizzes]
        .sort((a, b) => a.orden - b.orden);
    }

    return response;
  } catch (error) {
    console.error(`Error fetching quizzes for chapter ${chapterId}:`, error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar los quizzes'
    });
    throw error;
  } finally {
    isLoadingUnits.value[chapterId] = false;
  }
};

const combinedUnits = (chapter) => {
  const lessons = chapter.lessons?.map(l => ({
    ...l,
    title: l.titulo || l.title, // Ensure title exists
    type: l.type || 'lesson' // Default to lesson if type not specified
  })) || [];

  const quizzes = chapter.quizzes?.map(q => ({
    ...q,
    title: q.titulo || q.title, // Ensure title exists
    type: q.type || 'quiz' // Default to quiz if type not specified
  })) || [];

  return [...lessons, ...quizzes].sort((a, b) => a.orden - b.orden);
};

onMounted(async () => {
  await fetchChapters();

  // Initialize SortableJS after data loads
  nextTick(() => {
    new Sortable(document.getElementById("chaptersList"), {
      animation: 150,
      ghostClass: "sortable-ghost",
      onEnd: function (evt) {
        reorderChapters(evt.to);
      }
    });
  });
});
</script>

<template>
  <div class="col-sm-6 pe-1">
    <div class="card h-100">
      <div class="card-header d-flex pb-2 flex-column flex-md-row">
        <div class="col-12 col-md-8">
          <h5 class="font-weight-bolder mb-0">
            <i class="fas fa-book-open pe-2"></i> Contenido
          </h5>
          <p class="text-sm mb-0">
            Arrastra y suelta para re ordenar los capitulos
          </p>
        </div>
        <!-- Column 2 (Button) -->
        <div class="col-12 col-md-4 d-flex justify-content-end align-items-center mt-2 mt-md-0">
          <button type="button" @click="addNewChapter({ title: 'Nuevo Capítulo', isFree: false, lessons: [] })"
            class="d-flex justify-content-center w-100 mt-2 mb-0 btn btn-sm btn-outline-dark me-lg-0 mt-lg-0">
            <i class="fas fa-plus-circle pe-2 d-flex align-items-center"></i>
            Agregar Capitulo
          </button>
        </div>
      </div>
      <div class="card-body pt-1 p-2 p-md-4">

        <div class="mt-0 mt-md-0" id="chaptersList" style="max-height: 75vh; overflow-y: auto;">
          <!-- Chapter Loop -->
          <div v-for="chapter in chapters" :key="chapter.id" class="my-2 mx-3 card bg-gray-200" :data-id="chapter.id">
            <!-- Chapter Header -->
            <div class="card-body px-3 py-2">
              <div class="row align-items-center">
                <!-- Left Column (Chapter title with grip icon) -->
                <div class="col-8 col-md-9 d-flex align-items-center">
                  <i class="fas fa-grip-vertical me-2 me-md-3 text-sm"></i>
                  <h6 class="mb-0 text-md-lg text-sm text-truncate">{{ chapter.titulo }}</h6>
                </div>
                
                <!-- Right Column (Badge and action buttons) -->
                <div class="col-4 col-md-3">
                  <div class="d-flex align-items-center justify-content-end">
                    <span v-if="!chapter.es_gratuito" class="badge badge-sm badge-dark me-2 me-md-3">
                      <i class="fas fa-star"></i>
                    </span>
                    <span v-if="chapter.hasPrerequisite" class="badge badge-sm badge-dark me-2 me-md-3">
                      <i class="fas fa-lock"></i>
                    </span>                    
                    <button class="btn btn-sm btn-link text-muted px-1 px-md-2 mb-0 me-2" @click="editChapter(chapter.id)">
                      <i class="fas fa-pen"></i>
                    </button>
                    <button class="btn btn-sm btn-link text-muted px-1 px-md-2 mb-0" @click="toggleChapter(chapter.id)">
                      <i class="fas fa-chevron-down" :class="{ 'rotate-180': chapter.isOpen }"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chapter Content (Lessons) -->
            <div v-if="chapter.isOpen" class="card-body p-0">
              <!-- Loading State for Units -->
              <div v-if="isLoadingUnits[chapter.id]" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
                <p class="text-sm text-muted mt-2">Cargando contenido...</p>
              </div>

              <!-- Combined Loop for Lessons and Quizzes -->
              <template v-else v-for="unit in combinedUnits(chapter)" :key="`${unit.type}-${unit.id}`">
                <div class="border-top">
                  <!-- Unit Header -->
                  <div class="px-4 py-1 d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <i v-if="unit.type === 'quiz'" class="me-2 fas fa-question-circle text-success"></i>
                      <i v-else class="me-2 fas fa-book text-primary"></i>
                      <span class="text-sm">{{ unit.titulo }} </span>
                    </div>
                    <div class="d-flex align-items-center">                      
                      <span v-if="unit.requiere_nota_minima" class="badge badge-sm badge-dark me-2 me-md-3">
                        <i class="fas fa-lock"></i>
                      </span>
                      <button class="btn btn-sm btn-link text-muted px-1 px-md-2 mb-0 me-2"
                        @click="unit.type === 'quiz' ? editQuiz(chapter.id, unit.id) : editLesson(chapter.id, unit.id)">
                        <i class="fas fa-pen"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Add Lesson/Quiz Button -->
              <div class="px-4 py-2 border-top text-center d-flex justify-content-center gap-2">
                <button class="btn btn-sm btn-outline-primary px-4 py-2"
                  :disabled="isCreatingLesson[chapter.id]"
                  @click="addNewLesson(chapter.id, { type: 'lesson', title: 'Nueva Lección', content: '' })">
                  <span v-if="isCreatingLesson[chapter.id]" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="fas fa-plus me-1"></i> Añadir Lección
                </button>
                <button class="btn btn-sm btn-outline-success px-4 py-2"
                  :disabled="isCreatingQuiz[chapter.id]"
                  @click="addNewQuiz(chapter.id, { type: 'quiz', title: 'Nuevo Quiz', content: '' })">
                  <span v-if="isCreatingQuiz[chapter.id]" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="fas fa-plus me-1"></i> Añadir Quiz
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="col-sm-6 ps-1 mt-2 mt-md-0">
    <!-- Chapter Editor -->
    <chapter-edit v-if="activeTab === 'chapter'" @units-reordered="handleUnitsReordered" :chapter-id="selectedChapterId"
      :existing-chapter="currentChapterData" @save-chapter="updateChapter" @create-chapter="addNewChapter"
      @delete-chapter="removeChapter" />

    <!-- Lesson Editor -->
    <lesson-edit v-else-if="activeTab === 'lesson'" :chapter-id="selectedChapterId" :lesson-id="selectedLessonId"
      :existing-lesson="currentLessonData" @save-lesson="updateLesson" @delete-lesson="removeLesson" />

    <!-- Quiz Editor -->
    <quiz-maker v-else-if="activeTab === 'quiz'" :chapter-id="selectedChapterId" :quizz-id="selectedLessonId"
      :existing-quiz="currentQuizData" @save-quiz="updateQuiz" @delete-quiz="removeQuiz" />

    <!-- Empty State -->
    <div v-else class="card h-100 d-flex align-items-center justify-content-center">
      <div class="text-center p-4">
        <i class="fas fa-book-open fa-3x text-muted mb-3"></i>
        <h5 class="mb-2">Selecciona un capítulo, lección o quiz para editar</h5>
        <p class="text-sm text-muted">O crea un nuevo capítulo</p>
        <button class="btn btn-dark" @click="addNewChapter({ title: 'Nuevo Capítulo', isFree: false, lessons: [] })">
          <i class="fas fa-plus me-2"></i> Nuevo Capítulo
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.sortable-ghost {
  background: #f8f9fa;
  opacity: 0.7;
}

.rotate-180 {
  transform: rotate(180deg);
}

</style>
